import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
import { Loading } from '../../components/Shared';

const STATUTS = ['brouillon','planifiee','en_cours','terminee','annulee'];
const STATUT_LABELS = {
  brouillon:  '✏️ Brouillon',
  planifiee:  '📅 Planifiée',
  en_cours:   '▶ En cours',
  terminee:   '✅ Terminée',
  annulee:    '✕ Annulée',
};
const STATUT_COLORS = {
  brouillon:  { bg: 'rgba(92,96,120,0.2)',    color: '#b8bdd0' },
  planifiee:  { bg: 'rgba(74,158,255,0.12)',  color: '#4a9eff' },
  en_cours:   { bg: 'rgba(245,158,11,0.15)',  color: '#f59e0b' },
  terminee:   { bg: 'rgba(52,211,153,0.15)',  color: '#34d399' },
  annulee:    { bg: 'rgba(248,113,113,0.15)', color: '#f87171' },
};
const TYPES = ['email','sms','courrier','appel','evenement','autre'];

function ModalCampagne({ camp, canaux, onSave, onClose }) {
  const isEdit = !!camp?.id;
  const [form, setForm] = useState({
    nom:             camp?.nom             || '',
    type_campagne:   camp?.type_campagne   || 'email',
    statut:          camp?.statut          || 'brouillon',
    canal_source_id: camp?.canal_source_id || '',
    date_debut:      camp?.date_debut?.slice(0,10) || '',
    date_fin:        camp?.date_fin?.slice(0,10)   || '',
    budget:          camp?.budget          || '',
    description:     camp?.description    || '',
    objectif:        camp?.objectif        || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    width: '100%', padding: '0.45rem 0.65rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box',
  };

  async function handleSave() {
    if (!form.nom) return setError('Nom requis');
    setSaving(true); setError('');
    try {
      const body = {
        ...form,
        budget:          form.budget ? parseFloat(form.budget) : null,
        canal_source_id: form.canal_source_id || null,
        date_debut:      form.date_debut || null,
        date_fin:        form.date_fin   || null,
      };
      if (isEdit) {
        await apiFetch(`/crm/campagnes/${camp.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await apiFetch('/crm/campagnes', { method: 'POST', body: JSON.stringify(body) });
      }
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ width: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Modifier la campagne' : 'Nouvelle campagne'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          <div className="form-grid-2">
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Nom *</label>
              <input style={inputStyle} value={form.nom}
                onChange={e => set('nom', e.target.value)}
                placeholder="Ex : Relance CACES printemps 2025" />
            </div>
            <div>
              <label className="form-label">Type</label>
              <select style={inputStyle} value={form.type_campagne}
                onChange={e => set('type_campagne', e.target.value)}>
                {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Statut</label>
              <select style={inputStyle} value={form.statut}
                onChange={e => set('statut', e.target.value)}>
                {STATUTS.map(s => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Date début</label>
              <input type="date" style={inputStyle} value={form.date_debut}
                onChange={e => set('date_debut', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Date fin</label>
              <input type="date" style={inputStyle} value={form.date_fin}
                onChange={e => set('date_fin', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Budget (€)</label>
              <input type="number" min="0" style={inputStyle} value={form.budget}
                onChange={e => set('budget', e.target.value)} placeholder="0" />
            </div>
            <div>
              <label className="form-label">Canal</label>
              <select style={inputStyle} value={form.canal_source_id || ''}
                onChange={e => set('canal_source_id', e.target.value)}>
                <option value="">—</option>
                {canaux.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Objectif</label>
              <input style={inputStyle} value={form.objectif}
                onChange={e => set('objectif', e.target.value)}
                placeholder="Ex : 50 nouvelles inscriptions" />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Description</label>
              <textarea style={{ ...inputStyle, height: 70, resize: 'vertical' }}
                value={form.description} onChange={e => set('description', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement…' : isEdit ? 'Modifier' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
}

const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtEur  = n => n ? Number(n).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) : '—';

export default function CRMCampagnes() {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [canaux, setCanaux]     = useState([]);
  const [filterStatut, setFilterStatut] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 100 });
    if (filterStatut) params.set('statut', filterStatut);
    apiFetch(`/crm/campagnes?${params}`)
      .then(r => { setItems(r.data || []); setTotal(r.total || 0); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [filterStatut]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/crm/canaux-source').then(r => setCanaux(r.data || [])).catch(() => {});
  }, []);

  async function handleDelete(id) {
    if (!window.confirm('Supprimer cette campagne ?')) return;
    try {
      await apiFetch(`/crm/campagnes/${id}`, { method: 'DELETE' });
      load();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  return (
    <div className="crm-section-page">
      <div className="crm-toolbar">
        <div className="crm-filters">
          <select className="crm-select" value={filterStatut}
            onChange={e => setFilterStatut(e.target.value)}>
            <option value="">Tous les statuts</option>
            {STATUTS.map(s => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
          </select>
        </div>
        <button className="btn btn-primary crm-btn-sm"
          onClick={() => { setEditing(null); setShowModal(true); }}>
          + Campagne
        </button>
      </div>

      {loading && <Loading />}
      {error && <div className="crm-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="crm-campagnes-grid">
            {items.length === 0 && <div className="crm-empty">Aucune campagne trouvée</div>}
            {items.map(camp => {
              const sc = STATUT_COLORS[camp.statut] || STATUT_COLORS.brouillon;
              return (
                <div key={camp.id} className="crm-campagne-card">
                  <div className="crm-campagne-card-top">
                    <span className="crm-badge" style={sc}>{STATUT_LABELS[camp.statut]}</span>
                    <span className="crm-text-sm" style={{ color: 'var(--text-tertiary)' }}>
                      {camp.type_campagne}
                    </span>
                  </div>
                  <div className="crm-campagne-card-title">{camp.nom}</div>
                  {camp.objectif && (
                    <div className="crm-campagne-card-objectif">🎯 {camp.objectif}</div>
                  )}
                  <div className="crm-campagne-card-meta">
                    {camp.date_debut && <span>📅 {fmtDate(camp.date_debut)}{camp.date_fin ? ` → ${fmtDate(camp.date_fin)}` : ''}</span>}
                    {camp.budget > 0  && <span>💶 {fmtEur(camp.budget)}</span>}
                    {camp.nb_destinataires > 0 && <span>👥 {camp.nb_destinataires} dest.</span>}
                  </div>
                  {camp.description && (
                    <div className="crm-campagne-card-desc">{camp.description}</div>
                  )}
                  <div className="crm-campagne-card-actions">
                    <button className="crm-card-btn"
                      onClick={() => { setEditing(camp); setShowModal(true); }}>✎ Modifier</button>
                    <button className="crm-card-btn crm-card-btn--danger"
                      onClick={() => handleDelete(camp.id)}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="crm-count">{total} campagne{total > 1 ? 's' : ''}</div>
        </>
      )}

      {showModal && (
        <ModalCampagne
          camp={editing}
          canaux={canaux}
          onSave={() => { setShowModal(false); load(); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
